import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import MaterialTrendsEdit from "../../components/WebEditor/MaterialTrendsEdit";
import BlogEditor from "../../components/WebEditor/BlogEditor";
import WebsiteReviews from "../../components/WebEditor/WebsiteReviews";
import LocationHoursEditor from "../../components/WebEditor/LocationHoursEditor";
import SubscribedUsers from "../../components/WebEditor/SubscribedUsers";
import WebleadReferrals from "../../components/WebEditor/WebleadReferrals";
import WebIcon from "@mui/icons-material/Web";
import { PageHeader, PageShell } from "../../components/UI/Page";

const WebsiteEditor = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedNav, setSelectedNav] = useState("Material Trends");

  // Synchronize state with the URL parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const navParam = params.get("nav");
    if (navParam) {
      setSelectedNav(navParam);
    }
  }, [location]);

  const navItems = [
    {
      id: "Material Trends",
      label: "Material Trends",
      description: "Refresh the homepage inspiration image.",
      render: () => <MaterialTrendsEdit />,
    },
    {
      id: "Blog Editor",
      label: "Blog Editor",
      description: "Create or update blog posts and thumbnails.",
      render: () => <BlogEditor />,
    },
    {
      id: "Website Reviews",
      label: "Website Reviews",
      description: "View recent customer feedback.",
      render: () => <WebsiteReviews />,
    },
    {
      id: "Weblead Referrals",
      label: "Weblead Referrals",
      description: "Filter referrals and export results.",
      render: () => <WebleadReferrals />,
    },
    {
      id: "Subscribed Users",
      label: "Subscribed Users",
      description: "Download newsletter sign-ups.",
      render: () => <SubscribedUsers />,
    },
    {
      id: "Location Hours",
      label: "Location Hours",
      description: "Edit operating hours for each branch.",
      render: () => (
        <LocationHoursEditor
          locations={[
            "Corporate",
            "Granger",
            "Mishawaka",
            "LaPorte",
            "Roseland",
            "Kendallville",
            "Cromwell",
            "Shipshewana",
            "Fort Wayne",
            "Elkhart",
            "Adrian",
            "Coloma",
            "Eau Claire",
            "Schoolcraft",
            "Three Oaks",
            "Delton",
            "Edgerton",
          ]}
        />
      ),
    },
  ];

  const renderView = () => navItems.find((item) => item.id === selectedNav)?.render();

  const handleNavClick = (view) => {
    setSelectedNav(view);
    navigate(`?nav=${view}`); // Update URL with the selected view
  };

  return (
    <PageShell size="2xl">
      <PageHeader
        icon={WebIcon}
        tone="slate"
        eyebrow="Admin"
        eyebrowTo="/admin"
        title="Keep marketing content fresh"
        description="Switch between tabs to edit homepage highlights, blog stories, customer reviews, referral reports, newsletter sign-ups, and location hours. Changes save instantly to keep the site current."
      />

      <div className="flex flex-col gap-6 lg:flex-row">
        <nav aria-label="Website editor sections" className="shrink-0 lg:w-72">
          <div className="app-card overflow-hidden rounded-2xl border lg:sticky lg:top-24">
            <h2 className="app-eyebrow border-b app-divider px-4 py-3">
              Sections
            </h2>
            <div className="flex gap-1 overflow-x-auto p-2 lg:flex-col lg:overflow-visible">
              {navItems.map((item) => {
                const active = selectedNav === item.id;
                return (
                  <button
                    type="button"
                    key={item.id}
                    onClick={() => handleNavClick(item.id)}
                    aria-current={active ? "page" : undefined}
                    className={`min-w-[11rem] shrink-0 rounded-xl px-3 py-2.5 text-left transition-colors lg:min-w-0 ${
                      active
                        ? "bg-sky-500/15 text-sky-700 dark:text-sky-300"
                        : "hover:bg-[color:var(--app-hover)]"
                    }`}
                  >
                    <div
                      className={`text-sm ${active ? "font-semibold" : "font-medium"}`}
                    >
                      {item.label}
                    </div>
                    <p className="app-muted mt-0.5 text-xs">
                      {item.description}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
        </nav>

        <div className="min-w-0 flex-1">
          <div className="app-card rounded-2xl border p-4 sm:p-6">
            {renderView()}
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default WebsiteEditor;
